import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { safeExternalHref } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import CustomerAccountPanel from "@/components/admin/CustomerAccountPanel";
import {
  ArrowLeft, Building2, Users as UsersIcon, FolderKanban, Activity,
  ShieldAlert, Loader2, Settings2, PowerOff, Power, Trash2, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "users", label: "Users" },
  { id: "projects", label: "Projects" },
  { id: "activity", label: "Activity" },
  { id: "profile", label: "Customer profile" },
  { id: "billing", label: "Billing & Entitlements" },
];

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
}

function Chip({ state, label }) {
  const map = { ok: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", active: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", disabled: "bg-amber-500/15 text-amber-300 border-amber-500/30", archived: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30", pending: "bg-blue-500/15 text-blue-300 border-blue-500/30" };
  return <span className={`px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider rounded border ${map[state] || "border-[var(--fop-border)]"}`}>{label || state}</span>;
}

function Stat({ label, value, sub }) {
  return (
    <div className="fop-surface-2 rounded-lg p-3 border border-[var(--fop-border)]">
      <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--fop-text-3)]">{label}</div>
      <div className="font-display text-2xl text-[var(--fop-text)] mt-0.5">{value}</div>
      {sub && <div className="text-[10px] font-mono text-[var(--fop-text-4)] mt-0.5">{sub}</div>}
    </div>
  );
}

export default function CustomerDetail() {
  const { tenantId } = useParams();
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [dash, setDash] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersMeta, setUsersMeta] = useState({ total_count: 0, has_more: false });
  const [projects, setProjects] = useState([]);
  const [projectsMeta, setProjectsMeta] = useState({ total_count: 0, has_more: false });
  const [activity, setActivity] = useState([]);
  const [activityMeta, setActivityMeta] = useState({ total_count: 0, has_more: false });
  const [working, setWorking] = useState(false);

  const isPlatformAdmin = !!user?.is_platform_admin;

  const loadDash = async () => {
    try { const r = await api.get(`/platform/tenants/${tenantId}/dashboard`); setDash(r.data); } catch (e) { toast.error("Failed to load tenant"); }
  };
  // FP-086 (2026-09-09 audit remediation) -- these endpoints now return
  // {items, total_count, has_more} instead of a bare (silently
  // truncated) array. `skip` lets "Load more" genuinely page through
  // the rest instead of only ever showing the first page.
  const loadUsers = async (skip = 0) => {
    try {
      const { data } = await api.get(`/platform/tenants/${tenantId}/users`, { params: { limit: 500, skip } });
      setUsers((prev) => (skip ? [...prev, ...(data.items || [])] : data.items || []));
      setUsersMeta({ total_count: data.total_count ?? 0, has_more: !!data.has_more, skip });
    } catch (e) { toast.error("Failed to load users"); }
  };
  const loadProjects = async (skip = 0) => {
    try {
      const { data } = await api.get(`/platform/tenants/${tenantId}/projects`, { params: { limit: 500, skip } });
      setProjects((prev) => (skip ? [...prev, ...(data.items || [])] : data.items || []));
      setProjectsMeta({ total_count: data.total_count ?? 0, has_more: !!data.has_more, skip });
    } catch (e) { toast.error("Failed to load projects"); }
  };
  const loadActivity = async (skip = 0) => {
    try {
      const { data } = await api.get(`/platform/tenants/${tenantId}/activity`, { params: { limit: 80, skip } });
      setActivity((prev) => (skip ? [...prev, ...(data.items || [])] : data.items || []));
      setActivityMeta({ total_count: data.total_count ?? 0, has_more: !!data.has_more, skip });
    } catch (e) { toast.error("Failed to load activity"); }
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => { if (isPlatformAdmin) loadDash(); }, [tenantId, isPlatformAdmin]);
  useEffect(() => {
    if (!isPlatformAdmin) return;
    if (tab === "users" && users.length === 0) loadUsers();
    if (tab === "projects" && projects.length === 0) loadProjects();
    if (tab === "activity" && activity.length === 0) loadActivity();
  }, [tab, isPlatformAdmin]);
  /* eslint-enable react-hooks/exhaustive-deps */

  if (!isPlatformAdmin) {
    return (
      <div className="p-10 max-w-xl mx-auto text-center" data-testid="customer-detail-denied">
        <ShieldAlert size={32} className="text-amber-400 mx-auto mb-3" />
        <p className="text-sm text-[var(--fop-text-3)]">Platform admins only.</p>
      </div>
    );
  }

  const toggleStatus = async (next) => {
    if (!window.confirm(`${next === "disabled" ? "Suspend" : "Activate"} ${dash?.tenant?.display_name}?`)) return;
    setWorking(true);
    try {
      await api.patch(`/platform/tenants/${tenantId}/status`, { status: next });
      toast.success(`Tenant ${next}`);
      await loadDash();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status change failed");
    } finally { setWorking(false); }
  };

  const reinvite = async () => {
    setWorking(true);
    try {
      // Iter 136 — call the dedicated resend endpoint (NOT /reset_owner,
      // which expects {email,name} and 422'd with a Pydantic list that
      // the toast couldn't render and crashed the page).
      const r = await api.post(`/platform/tenants/${tenantId}/resend_owner_invite`, {});
      const d = r?.data || {};
      if (d.email_sent) {
        toast.success(`Owner invite re-sent to ${d?.invitation?.owner_email || "owner"}`);
      } else {
        toast.error(`Invite saved but email failed: ${d.email_error || "unknown error"}`);
      }
    } catch (e) {
      // Defensive: FastAPI 422 returns detail as an array of objects which
      // React can't render. Always coerce to a string before toasting.
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => d?.msg || JSON.stringify(d)).join("; ")
          : (detail ? JSON.stringify(detail) : (e?.message || "Reinvite failed"));
      toast.error(msg);
    } finally { setWorking(false); }
  };

  if (!dash) return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="customer-detail-loading">
      <div className="font-mono text-xs text-[var(--fop-text-3)] inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading customer…</div>
    </div>
  );

  const t = dash.tenant || {};
  const c = dash.counts || {};

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="customer-detail-page">
      <Link to="/platform" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--fop-text)] mb-3" data-testid="cd-back">
        <ArrowLeft size={12} /> Customer Management
      </Link>

      <div className="fop-surface-2 p-5 rounded-lg mb-3 flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--fop-text-3)]">Customer · {t.slug}</div>
          <h1 className="font-display text-2xl text-[var(--fop-text)] inline-flex items-center gap-2">
            <Building2 size={20} className="text-[var(--fop-primary)]" /> {t.display_name}
          </h1>
          <div className="text-xs text-[var(--fop-text-3)] mt-1">{t.contact_email || "—"}{t.website && safeExternalHref(t.website) && <span> · <a href={safeExternalHref(t.website)} target="_blank" rel="noreferrer" className="hover:underline">{t.website.replace(/^https?:\/\//, "")}</a></span>}</div>
        </div>
        <div className="inline-flex items-center gap-2">
          <Chip state={t.status} label={t.status} />
          {t.status === "active" ? (
            <button onClick={() => toggleStatus("disabled")} disabled={working} className="btn btn-ghost text-[11px] inline-flex items-center gap-1.5 text-amber-400" data-testid="cd-suspend"><PowerOff size={11} /> Suspend</button>
          ) : (
            <button onClick={() => toggleStatus("active")} disabled={working} className="btn btn-ghost text-[11px] inline-flex items-center gap-1.5 text-emerald-400" data-testid="cd-activate"><Power size={11} /> Activate</button>
          )}
          <button onClick={reinvite} disabled={working} className="btn btn-ghost text-[11px] inline-flex items-center gap-1.5" data-testid="cd-reinvite"><RefreshCw size={11} /> Re-send owner invite</button>
        </div>
      </div>

      <div className="fop-surface-2 rounded-lg p-1 mb-3 inline-flex" data-testid="cd-tabs">
        {TABS.map((tb) => (
          <button key={tb.id} onClick={() => setTab(tb.id)} data-testid={`cd-tab-${tb.id}`} className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider rounded ${tab === tb.id ? "bg-[var(--fop-primary)] text-black" : "text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"}`}>
            {tb.label}
          </button>
        ))}
      </div>

      {(tab === "profile" || tab === "billing") && (
        <CustomerAccountPanel key={tenantId + ":" + tab} tenantId={tenantId} section={tab} />
      )}
      {tab === "overview" && (
        <div className="space-y-3" data-testid="cd-overview">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Users" value={`${c.active_users || 0}/${c.users || 0}`} sub="active / total" />
            <Stat label="Projects" value={c.projects || 0} />
            <Stat label="Sheets" value={c.sheets || 0} />
            <Stat label="Packages" value={c.packages || 0} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Active share links" value={c.share_links || 0} />
            <Stat label="Pending invites" value={c.invitations_pending || 0} />
            <Stat label="Intake pending" value={c.intake_pending || 0} />
            <Stat label="Discoveries" value={c.discoveries || 0} />
          </div>
          {dash.last_activity && (
            <div className="fop-surface-2 rounded-lg p-3 border border-[var(--fop-border)]">
              <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--fop-text-3)] mb-1">Last activity</div>
              <div className="text-xs text-[var(--fop-text-2)] truncate" data-testid="cd-last-activity">{dash.last_activity.message || dash.last_activity.type || dash.last_activity.action}</div>
              <div className="text-[10px] font-mono text-[var(--fop-text-4)] mt-0.5">{fmt(dash.last_activity.created_at)}</div>
            </div>
          )}
        </div>
      )}

      {tab === "users" && (
        <div className="fop-surface-2 rounded-lg overflow-hidden" data-testid="cd-users">
          <table className="w-full text-xs">
            <thead className="bg-[var(--fop-surface)] text-[var(--fop-text-3)]">
              <tr className="text-left">
                <th className="px-3 py-2 font-mono uppercase tracking-wider text-[10px]">User</th>
                <th className="px-3 py-2 font-mono uppercase tracking-wider text-[10px]">Role</th>
                <th className="px-3 py-2 font-mono uppercase tracking-wider text-[10px]">Status</th>
                <th className="px-3 py-2 font-mono uppercase tracking-wider text-[10px]">Last login</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-[var(--fop-border)]" data-testid={`cd-user-${u.id}`}>
                  <td className="px-3 py-2"><div className="text-[var(--fop-text)]">{u.email}</div><div className="text-[10px] font-mono text-[var(--fop-text-4)]">{u.name}</div></td>
                  <td className="px-3 py-2 font-mono text-[var(--fop-text-2)]">{u.role}</td>
                  <td className="px-3 py-2"><Chip state={u.status} /></td>
                  <td className="px-3 py-2 text-[10px] font-mono text-[var(--fop-text-4)]">{fmt(u.last_login_at)}</td>
                </tr>
              ))}
              {users.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-[var(--fop-text-3)]">No users yet.</td></tr>}
            </tbody>
          </table>
          <ListFooter meta={usersMeta} shownCount={users.length} onLoadMore={() => loadUsers(usersMeta.skip ? usersMeta.skip + 500 : 500)} testid="cd-users-load-more" />
        </div>
      )}

      {tab === "projects" && (
        <div className="fop-surface-2 rounded-lg overflow-hidden" data-testid="cd-projects">
          <table className="w-full text-xs">
            <thead className="bg-[var(--fop-surface)] text-[var(--fop-text-3)]">
              <tr className="text-left">
                <th className="px-3 py-2 font-mono uppercase tracking-wider text-[10px]">Project</th>
                <th className="px-3 py-2 font-mono uppercase tracking-wider text-[10px]">Client</th>
                <th className="px-3 py-2 font-mono uppercase tracking-wider text-[10px]">Status</th>
                <th className="px-3 py-2 font-mono uppercase tracking-wider text-[10px]">Created</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <tr key={p.id} className="border-t border-[var(--fop-border)]" data-testid={`cd-project-${p.id}`}>
                  <td className="px-3 py-2"><div className="text-[var(--fop-text)]">{p.name}</div><div className="text-[10px] font-mono text-[var(--fop-text-4)]">{p.number || "—"}</div></td>
                  <td className="px-3 py-2 text-[var(--fop-text-2)]">{p.client || "—"}</td>
                  <td className="px-3 py-2 text-[var(--fop-text-2)]">{p.status || "—"}{p.internal ? " · internal" : ""}</td>
                  <td className="px-3 py-2 text-[10px] font-mono text-[var(--fop-text-4)]">{fmt(p.created_at)}</td>
                </tr>
              ))}
              {projects.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-[var(--fop-text-3)]">No projects yet.</td></tr>}
            </tbody>
          </table>
          <ListFooter meta={projectsMeta} shownCount={projects.length} onLoadMore={() => loadProjects(projectsMeta.skip ? projectsMeta.skip + 500 : 500)} testid="cd-projects-load-more" />
        </div>
      )}

      {tab === "activity" && (
        <div className="fop-surface-2 rounded-lg overflow-hidden" data-testid="cd-activity">
          <table className="w-full text-xs">
            <thead className="bg-[var(--fop-surface)] text-[var(--fop-text-3)]">
              <tr className="text-left">
                <th className="px-3 py-2 font-mono uppercase tracking-wider text-[10px]">When</th>
                <th className="px-3 py-2 font-mono uppercase tracking-wider text-[10px]">Action</th>
                <th className="px-3 py-2 font-mono uppercase tracking-wider text-[10px]">Target</th>
                <th className="px-3 py-2 font-mono uppercase tracking-wider text-[10px]">Outcome</th>
              </tr>
            </thead>
            <tbody>
              {activity.map((a) => (
                <tr key={a.id} className={`border-t border-[var(--fop-border)] ${a.success === false ? "bg-red-500/5" : ""}`} data-testid={`cd-activity-${a.id}`}>
                  <td className="px-3 py-2 text-[10px] font-mono text-[var(--fop-text-4)]">{fmt(a.created_at)}</td>
                  <td className="px-3 py-2 font-mono text-[var(--fop-text-2)]">{a.action}</td>
                  <td className="px-3 py-2 text-[10px] font-mono text-[var(--fop-text-4)]">{a.target_type ? `${a.target_type} · ${(a.target_id || "").slice(0, 12)}…` : "—"}</td>
                  <td className="px-3 py-2 text-[10px] font-mono">{a.success === false ? <span className="text-red-300">FAILED</span> : <span className="text-emerald-300">ok</span>}{a.severity && ` · ${a.severity}`}</td>
                </tr>
              ))}
              {activity.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-[var(--fop-text-3)]">No activity yet.</td></tr>}
            </tbody>
          </table>
          <ListFooter meta={activityMeta} shownCount={activity.length} onLoadMore={() => loadActivity(activityMeta.skip ? activityMeta.skip + 80 : 80)} testid="cd-activity-load-more" />
        </div>
      )}
    </div>
  );
}


// FP-086 (2026-09-09 audit remediation) -- honest disclosure + a real
// way to see the rest, instead of a hard cap silently hiding records
// with no indication more exist.
function ListFooter({ meta, shownCount, onLoadMore, testid }) {
  if (!meta || meta.total_count <= shownCount) return null;
  return (
    <div className="px-3 py-2 border-t border-[var(--fop-border)] flex items-center justify-between text-[11px] font-mono text-[var(--fop-text-3)]" data-testid={testid}>
      <span data-testid={`${testid}-count`}>Showing {shownCount} of {meta.total_count}</span>
      {meta.has_more && (
        <button onClick={onLoadMore} className="uppercase tracking-wider text-[var(--fop-primary)] hover:underline" data-testid={`${testid}-btn`}>
          Load more
        </button>
      )}
    </div>
  );
}
